import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Geist } from "next/font/google";
import { Slot } from "@radix-ui/react-slot";
import {
  ArrowRight,
  ArrowUpRight,
  CheckCircle,
  XCircle,
  Star,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  CalendarIcon,
  Trash2,
} from "lucide-react";

export const geistSans = Geist({
  variable: "--font-geist-sans",
  subsets: ["latin"],
  fallback: ["system-ui", "sans-serif"],
  weight: ["400", "500", "600", "700", "800", "900"],
  display: "swap",
});

const buttonVariants = cva(
  "inline-flex items-center disabled:opacity-50 cursor-pointer justify-center gap-2.5 whitespace-nowrap rounded-md font-medium transition-all  disabled:cursor-not-allowed! [&_svg]:pointer-events-none focus-visible:ring-offset-2 [&_svg:not([class*='size-'])]:size-5 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        primary:
          "bg-pink-primary focus-visible:ring-pink-primary/50  text-white-primary hover:pink-overlay duration-300 ease-out",
        secondary:
          "bg-white-primary border border-light-primary text-dark-primary hover:bg-dark-primary hover:text-white-primary duration-300 ease-out focus-visible:ring-light-primary/90 ",
        light:
          "bg-secondary-light focus-visible:ring-pink-light/20 text-dark-primary hover:light-overlay duration-300 ease-out",
        rounded:
          "bg-dark-primary focus-visible:ring-dark-primary text-white-primary hover:dark-overlay duration-300 ease-out",
        slider:
          "bg-dark-primary/60 focus-visible:ring-dark-primary/50 text-white-primary hover:bg-dark-primary/95 duration-300 ease-out",
        danger:
          "bg-red-500 text-white-primary hover:bg-red-600 duration-300 ease-in-out focus-visible:ring-red-600/50 ",
      },
      size: {
        lg: "py-4.5 px-5 text-lg",
        md: "px-4 py-3",
        sm: "py-1.5 px-2.5 text-sm",
        icon: "p-2.5 rounded-full",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "lg",
    },
  }
);

const iconMap = {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  CheckCircle,
  XCircle,
  Star,
  ChevronLeft,
  ChevronRight,
  Plus,
  Check,
  CalendarIcon,
  Trash2,
};

type IconName = keyof typeof iconMap;

export type ButtonProps = React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    iconLeftName?: IconName;
    iconRightName?: IconName;
  } & {
    asChild?: boolean;
  };

function Button({
  children,
  className,
  variant,
  size,
  disabled = false,
  iconLeftName,
  iconRightName,
  asChild,
  ...props
}: ButtonProps) {
  const IconLeft = iconLeftName ? iconMap[iconLeftName] : null;
  const IconRight = iconRightName ? iconMap[iconRightName] : null;
  const Comp = asChild ? Slot : "button";

  return (
    <Comp
      data-slot="button"
      className={cn(
        geistSans.className,
        buttonVariants({ variant, size, className }),
        variant === "primary" && disabled ? "bg-pink-secondary" : ""
      )}
      disabled={disabled}
      {...props}
    >
      <>
        {IconLeft && <IconLeft aria-hidden="true" size={20} />}
        {children}
        {IconRight && <IconRight aria-hidden="true" size={20} />}
      </>
    </Comp>
  );
}

export { buttonVariants };
export default Button;
